/* Hand a contract over to the Project Studio (studio.studiogy.design) to open a
   job in the Bid Manager or the Project Manager.

   The Studio already imports projects from Atelier at /import, so we send the
   SAME payload shape (see atelier/src/lib/bridge.ts → StudioHandoff and
   bidroom/src/lib/atelierImport.ts → readAtelierHandoff), tagged source:"contract".
   It also posts "project-studio:created" back to the opener, which we listen for
   so the contract can remember the job it opened.

   URL-param handoff (gzip + base64url, falling back to raw base64) — no shared
   secret, and it survives embedded browsers. */

import type { ProjectBrief } from "../contracts/types";

const STUDIO_URLS = { prod: "https://studio.studiogy.design", dev: "http://localhost:4347" };
export function studioUrl(): string {
  if (typeof location !== "undefined" && location.hostname === "localhost") return STUDIO_URLS.dev;
  return STUDIO_URLS.prod;
}
export function studioOrigin(): string { try { return new URL(studioUrl()).origin; } catch { return STUDIO_URLS.prod; } }

function b64encode(obj: unknown): string { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }

/** A drawing register raw-encodes to ~16KB of URL, which browsers refuse to
 *  navigate to. Gzip takes it to ~2KB; base64url avoids percent-escaping. */
async function gzipB64url(obj: unknown): Promise<string | null> {
  try {
    if (typeof CompressionStream === "undefined") return null;
    const bytes = new TextEncoder().encode(JSON.stringify(obj));
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream("gzip"));
    const out = new Uint8Array(await new Response(stream).arrayBuffer());
    let bin = "";
    for (let i = 0; i < out.length; i++) bin += String.fromCharCode(out[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch { return null; }
}

export type StudioTarget = "pm" | "bid";
export interface StudioLinkRef { id: string; code: string; url: string; sheets: number; at: string }

export function briefToStudioHandoff(b: ProjectBrief, target: StudioTarget) {
  const design = b.standard === "AIA_B101_2017";
  // The Studio's budget is the construction cost — on a design appointment the
  // contract price is only our fee, so send the estimated Cost of the Work.
  const money = (design ? b.estimatedConstructionCost : b.contractPrice) || b.contractPrice;
  return {
    v: 1 as const,
    source: "contract",
    target,
    // Keyed back to this contract so the Studio's link-back lands on the right one.
    atelierProjectId: b.contractReference || b.projectName || "",
    name: b.projectName || "Untitled project",
    code: b.contractReference || "",
    type: design ? "Design appointment" : "Construction",
    location: b.projectLocation || "",
    area: "",
    budget: Number.isFinite(money?.amount as number) ? (money as any).amount : 0,
    currency: money?.currency || "USD",
    description: b.worksDescription || "",
    client: {
      name: b.employer?.representative || b.employer?.name || "",
      company: b.employer?.name || "",
      email: (b.employer as any)?.email || "",
    },
    register: (b.drawings || [])
      .filter((d) => d && (d.number || d.title))
      .map((d) => ({
        code: d.number || "—",
        title: d.title || "—",
        discipline: d.discipline || "Architecture",
        revision: d.revision,
      })),
    backUrl: typeof location !== "undefined" ? `${location.origin}/?go=contract` : "",
  };
}

/** Open the Studio's import screen with this contract as a Bid/PM job.
 *  The tab is opened synchronously (compressing first would spend the user
 *  gesture and get the popup blocked), then pointed at the payload. */
export function sendToStudio(b: ProjectBrief, target: StudioTarget) {
  const w = window.open("", "_blank");
  const payload = briefToStudioHandoff(b, target);
  void gzipB64url(payload).then((gz) => {
    const url = gz
      ? `${studioUrl()}/import?d=${gz}`
      : `${studioUrl()}/import?data=${encodeURIComponent(b64encode(payload))}`;
    if (w && !w.closed) w.location.replace(url);
    else window.location.href = url; // popup blocked — go in this tab
  });
}

/** Listen for "job created" from the Studio tab we opened. */
export function listenForStudioLink(onLink: (target: StudioTarget, ref: StudioLinkRef) => void): () => void {
  const handler = (e: MessageEvent) => {
    if (e.origin !== studioOrigin()) return; // only our studio may link back
    const d: any = e.data;
    if (!d || d.type !== "project-studio:created" || !d.id) return;
    onLink(d.target === "bid" ? "bid" : "pm", {
      id: String(d.id), code: String(d.code || ""), url: String(d.url || ""), sheets: Number(d.sheets) || 0, at: new Date().toISOString(),
    });
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}
